import { create } from "zustand";

import type {
  TopicReportTab,
  TopicReportSidebarTab,
  TopicReportTakeawaysSortOrder,
} from "@app/analytics/TopicReport/types";

interface TopicReportStateValues {
  activeTimelineItemId: string | null;
  selectedEntityId: string | null;
  selectedSidebarTab: TopicReportSidebarTab;
  selectedTab: TopicReportTab;
  shareDialogOpen: boolean;
  showRemoveFeedbackModal: boolean;
  sidebarExpanded: boolean;
  takeawaysSortOrder: TopicReportTakeawaysSortOrder;
}

interface TopicReportState extends TopicReportStateValues {
  resetState: () => void;
  setSidebarExpanded: (expand: boolean) => void;
  setActiveTimelineItemId: (id: string | null) => void;
  setSelectedEntityId: (entityId: string | null) => void;
  setSelectedSidebarTab: (tab: TopicReportSidebarTab) => void;
  setSelectedTab: (tab: TopicReportTab) => void;
  setShareDialogOpen: (open: boolean) => void;
  setShowRemoveFeedbackModal: (show: boolean) => void;
  setTakeawaysSortOrder: (order: TopicReportTakeawaysSortOrder) => void;
}

const DEFAULT_STATE: TopicReportStateValues = {
  activeTimelineItemId: null,
  selectedEntityId: null,
  selectedSidebarTab: "overview",
  selectedTab: "notes",
  shareDialogOpen: false,
  showRemoveFeedbackModal: false,
  sidebarExpanded: true,
  takeawaysSortOrder: "rank",
};

const useTopicReportStore = create<TopicReportState>((set) => ({
  ...DEFAULT_STATE,
  resetState: () =>
    set((state) => ({
      ...DEFAULT_STATE,
      // maintain basic UI state across reports
      sidebarExpanded: state.sidebarExpanded,
      takeawaysSortOrder: state.takeawaysSortOrder,
    })),
  setActiveTimelineItemId: (activeTimelineItemId) =>
    set({ activeTimelineItemId }),
  setSelectedEntityId: (selectedEntityId) =>
    set({
      selectedTab: "sources",
      selectedSidebarTab: "sources",
      selectedEntityId,
      sidebarExpanded: true,
    }),
  setSelectedSidebarTab: (selectedSidebarTab) => set({ selectedSidebarTab }),
  setSelectedTab: (selectedTab) =>
    set((state) => ({
      selectedTab,
      selectedSidebarTab:
        selectedTab === "notes" ? state.selectedSidebarTab : selectedTab,
    })),
  setShareDialogOpen: (open) => set({ shareDialogOpen: open }),
  setShowRemoveFeedbackModal: (show) => set({ showRemoveFeedbackModal: show }),
  setSidebarExpanded: (sidebarExpanded) => set({ sidebarExpanded }),
  setTakeawaysSortOrder: (takeawaysSortOrder) => set({ takeawaysSortOrder }),
}));

export default useTopicReportStore;
